import type { Author, ContractedBook } from '@/data/types';

export const FORFATTER_EVENT_COOLDOWN_DAGER = 14;

export type ForfatterEventVariant =
  | 'plagiat'
  | 'skrivesperre'
  | 'sabbatical'
  | 'drikke';

export const FORFATTER_EVENT_PREFIX = 'synth_forfatter_';

export function byggForfatterEventId(
  variant: ForfatterEventVariant,
  forfatterId: string,
  dag: number,
): string {
  return `${FORFATTER_EVENT_PREFIX}${variant}_${forfatterId}_${dag}`;
}

export function erForfatterEventId(eventId: string): boolean {
  return eventId.startsWith(FORFATTER_EVENT_PREFIX);
}

export function variantFraForfatterEventId(
  eventId: string,
): ForfatterEventVariant | null {
  if (!eventId.startsWith(FORFATTER_EVENT_PREFIX)) return null;
  const rest = eventId.slice(FORFATTER_EVENT_PREFIX.length);
  const variant = rest.split('_')[0];
  if (
    variant === 'plagiat' ||
    variant === 'skrivesperre' ||
    variant === 'sabbatical' ||
    variant === 'drikke'
  ) {
    return variant;
  }
  return null;
}

// Trigger-sannsynligheter (per kvalifisert forfatter per dag)
export const PLAGIAT_SJANSE = 0.01;
export const PLAGIAT_STRESS_TERSKEL = 60;

export const SKRIVESPERRE_SJANSE = 0.02;
export const SKRIVESPERRE_STRESS_TERSKEL = 70;
export const SKRIVESPERRE_FORSINKELSE_DAGER = 14;

export const SABBATICAL_SJANSE = 0.005;
export const SABBATICAL_EGO_TERSKEL = 60;
export const SABBATICAL_MIN_UTGITTE = 3;
export const SABBATICAL_LENGDE_DAGER = 120;
export const SABBATICAL_SMALER_LENGDE_DAGER = 60;

export const DRIKKE_SJANSE = 0.005;
export const DRIKKENIVAA_TERSKEL = 80;

// Stress-tikking
export const STRESS_PER_DAG_UTGIS_REDIGERES = 5;
export const STRESS_DAARLIG_ANMELDELSE = 10;
export const STRESS_SKANDALE_IGNORER = 15;

// Ego-tikking
export const EGO_POS_ANMELDELSE = 10;
export const EGO_BRAGE_NOMINASJON = 20;
export const EGO_BRAGE_VINST = 30;

// Drikkenivå-tikking
export const DRIKKE_DAGLIG_HOEY_STRESS = 3;
export const DRIKKE_STRESS_TERSKEL = 70;
export const DRIKKE_DAARLIG_ANMELDELSE = 5;

// Anmeldelses-terskler
export const ANMELDELSE_DAARLIG_TERSKEL = 3; // < 3
export const ANMELDELSE_GOD_TERSKEL = 5; // >= 5

export type ForfatterEventBetingelseKontekst = {
  dag: number;
  forfatter: Author;
  utgitteBoker: ContractedBook[];
  iProduksjon: ContractedBook[];
};

export function erKvalifisertForPlagiat(
  ctx: ForfatterEventBetingelseKontekst,
): boolean {
  if (ctx.forfatter.stress < PLAGIAT_STRESS_TERSKEL) return false;
  return harUtgittBok(ctx.forfatter.id, ctx.utgitteBoker, ctx.iProduksjon);
}

export function erKvalifisertForSkrivesperre(
  ctx: ForfatterEventBetingelseKontekst,
): boolean {
  if (ctx.forfatter.stress < SKRIVESPERRE_STRESS_TERSKEL) return false;
  return harBokIPipeline(ctx.forfatter.id, ctx.iProduksjon);
}

export function erKvalifisertForSabbatical(
  ctx: ForfatterEventBetingelseKontekst,
): boolean {
  if (ctx.forfatter.ego < SABBATICAL_EGO_TERSKEL) return false;
  if (ctx.forfatter.paaKurs) return false;
  const utgitte = antallUtgitteForFofatter(
    ctx.forfatter.id,
    ctx.utgitteBoker,
    ctx.iProduksjon,
  );
  return utgitte >= SABBATICAL_MIN_UTGITTE;
}

export function erKvalifisertForDrikke(
  ctx: ForfatterEventBetingelseKontekst,
): boolean {
  return ctx.forfatter.drikkenivaa >= DRIKKENIVAA_TERSKEL;
}

function harUtgittBok(
  forfatterId: string,
  utgitteBoker: ContractedBook[],
  iProduksjon: ContractedBook[],
): boolean {
  for (const b of utgitteBoker) {
    if (b.authorId === forfatterId) return true;
  }
  for (const b of iProduksjon) {
    if (b.authorId === forfatterId && b.status === 'utgitt') return true;
  }
  return false;
}

function harBokIPipeline(
  forfatterId: string,
  iProduksjon: ContractedBook[],
): boolean {
  for (const b of iProduksjon) {
    if (b.authorId !== forfatterId) continue;
    if (
      b.status === 'kontrahert' ||
      b.status === 'redigeres' ||
      b.status === 'redigert' ||
      b.status === 'designes' ||
      b.status === 'designet' ||
      b.status === 'utgis'
    ) {
      return true;
    }
  }
  return false;
}

function antallUtgitteForFofatter(
  forfatterId: string,
  utgitteBoker: ContractedBook[],
  iProduksjon: ContractedBook[],
): number {
  let n = 0;
  for (const b of utgitteBoker) {
    if (b.authorId === forfatterId) n += 1;
  }
  for (const b of iProduksjon) {
    if (b.authorId === forfatterId && b.status === 'utgitt') n += 1;
  }
  return n;
}

export function clampForfatterStat(n: number): number {
  if (n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n);
}
